/* assets/section-slideshow.js
 * Slice 11 Phase C — slideshow carousel.
 * Self-loaded via <script defer> from sections/slideshow.liquid when section.blocks.size > 1.
 *
 * Architecture: class-toggle (.slide--active). All slides absolute-positioned at the
 * same coordinates inside .section-slideshow__track. Exactly one slide has
 * .slide--active at any time. Phase C.5 layers transitions over this base:
 *   - JS sets data-direction="next"|"prev" on root before active-class flip.
 *   - JS adds .slide--outgoing to leaving slide for transitionDuration ms.
 *   - JS sets data-transitioning on root for the same window (curtain_reveal
 *     animation rule gates on it so the keyframe doesn't fire at first paint).
 *   - .section-slideshow--initialized flag set at end of init to gate the
 *     fade/slide transition CSS rules from running on initial render.
 *
 * Per-slideshow state lives in initSlideshow(rootEl) closure. Multiple slideshows
 * on one page operate independently.
 *
 * IIFE-dedup-guarded per Rule J12.
 *
 * Lifecycle:
 *   - DOMContentLoaded -> initAll(document)
 *   - shopify:section:load -> initAll(event.target)  (customizer hot-reload)
 *
 * Pause behaviors when autoplay enabled:
 *   - Hover (mouseenter/leave) -> only when pause_on_hover === true
 *   - Focus (focusin/focusout) -> always (APG accessibility)
 *   - Off-screen (IntersectionObserver, threshold 0.5)
 *   - prefers-reduced-motion (matchMedia) -> autoplay never starts
 *
 * Phase C.5 transition lifecycle:
 *   - goToSlide(idx, fromUserAction, direction): 3rd param is 'next'|'prev'|'jump'.
 *   - At start of every goToSlide, any in-flight state.outgoingTimer is cleared,
 *     lingering .slide--outgoing class is removed, and data-transitioning is
 *     stripped (rapid-click safety).
 *   - After active-class flip, leaving slide gets .slide--outgoing and root gets
 *     data-transitioning for transitionDuration ms; both cleared in setTimeout.
 *   - Same-slide user action (click on the active dot): data-transitioning is
 *     cleared synchronously since no real transition runs.
 *   - Reduced-motion handled in CSS (@media query nullifies durations); JS only
 *     branches on prefersReducedMotion for autoplay-start gating.
 *
 * Loop semantics:
 *   - enable_loop === true:  prev/next wrap; autoplay wraps
 *   - enable_loop === false: prev/next clamp with disabled state at edges;
 *                            autoplay STOPS when it reaches the last slide
 *                            (does not wrap; F9 amendment)
 *
 * ARIA pagination labels:
 *   Track carries data-pagination-label as a template with __CURRENT__ and __TOTAL__
 *   placeholders (Rule L11 — Liquid output tags can't contain literal { }).
 *   JS interpolates per slide on init and after each goToSlide.
 */
(function () {
  'use strict';

  if (window.__BLOCKDEDUP_SLIDESHOW__) {
    return;
  }
  window.__BLOCKDEDUP_SLIDESHOW__ = true;

  function initSlideshow(rootEl) {
    var trackEl = rootEl.querySelector('[data-slideshow-track]');
    if (!trackEl) {
      return;
    }

    var slideEls = Array.prototype.slice.call(trackEl.querySelectorAll('.slide'));
    if (slideEls.length <= 1) {
      return;
    }

    var prevBtn = rootEl.querySelector('[data-slideshow-prev]');
    var nextBtn = rootEl.querySelector('[data-slideshow-next]');
    var progressItems = Array.prototype.slice.call(rootEl.querySelectorAll('[data-slideshow-progress-item]'));

    var enableAutoplay = rootEl.getAttribute('data-autoplay-enabled') === 'true';
    var autoplayInterval = (parseInt(rootEl.getAttribute('data-autoplay-interval'), 10) || 6) * 1000;
    var enableLoop = rootEl.getAttribute('data-loop-enabled') === 'true';
    var pauseOnHover = rootEl.getAttribute('data-pause-on-hover') === 'true';
    var transitionDuration = parseInt(rootEl.getAttribute('data-transition-duration'), 10) || 600;

    var paginationLabelTemplate = trackEl.getAttribute('data-pagination-label') || '';

    var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var state = {
      currentIdx: 0,
      total: slideEls.length,
      autoplayTimer: null,
      autoplayDone: false,
      outgoingTimer: null,
      isHovered: false,
      isFocused: false,
      isVisible: true,
      isPageVisible: !document.hidden,
      isWindowFocused: document.hasFocus()
    };

    // Phase D.2 — peek-layout state extension.
    // state.layout drives every layout-specific branch in this file. data-layout
    // is emitted unconditionally on the section root (full_bleed/split/peek/cardstack);
    // future Phase E layouts read the same field.
    state.layout = rootEl.getAttribute('data-layout') || 'full_bleed';
    state.scrollEndTimer = null;
    state.userScrolling = false;
    state.peekObserver = null;

    // Phase D.3 — cardstack-layout state extension.
    // Stack array holds slide indices in top-of-stack-first order. Drag state
    // tracks an active Pointer Events session on the top card; commit timer
    // matches the CSS transition duration so the inline off-screen transform
    // animation finishes before the stack array rotates and updateStackVisuals
    // strips the inline transform. cardstackAnimDuration gates to 0 under
    // prefers-reduced-motion so the rotation is instant rather than waiting
    // 280ms for a CSS transition that has been disabled.
    state.stack = [];
    state.dragActive = false;
    state.dragPointerId = null;
    state.dragStartX = 0;
    state.dragStartY = 0;
    state.dragDx = 0;
    state.dragDy = 0;
    state.dragReleasing = false;
    state.cardstackTimer = null;
    state.cardstackAnimDuration = prefersReducedMotion ? 0 : 280;

    function setSlideAria(slideEl, idx) {
      var label = paginationLabelTemplate
        .replace(/__CURRENT__/g, String(idx + 1))
        .replace(/__TOTAL__/g, String(state.total));
      if (label) {
        slideEl.setAttribute('aria-label', label);
      }
    }

    function updateArrowDisabled() {
      if (enableLoop) {
        if (prevBtn) prevBtn.disabled = false;
        if (nextBtn) nextBtn.disabled = false;
        return;
      }
      if (prevBtn) prevBtn.disabled = (state.currentIdx === 0);
      if (nextBtn) nextBtn.disabled = (state.currentIdx === state.total - 1);
    }

    // Phase D.2 — extracted from prior inline goToSlide + init-reconciliation
    // loops. Sets .slide--active class + aria-hidden + per-slide pagination
    // ARIA label on every slide, and aria-selected + tabindex on every progress
    // item. In peek, the active class still toggles (drives dot active state +
    // autoplay sequencing) but no CSS rule gates visibility on it.
    // F5 (M2) — in adapt_each_image mode each slide emits its own inline
    // --slide-aspect-ratio-* vars, but custom properties inherit downward only,
    // so the parent track never receives them and collapses to auto height.
    // Copy the active slide's inline aspect vars onto the track on every
    // activation. Self-gating: those vars exist inline on slides ONLY in
    // adapt_each_image mode, so this no-ops in every other height mode.
    // peek/cardstack manage their own layout and are skipped.
    function syncAdaptTrackAspect(idx) {
      if (state.layout === 'peek' || state.layout === 'cardstack') return;
      var active = slideEls[idx];
      if (!active || !trackEl) return;
      var d = active.style.getPropertyValue('--slide-aspect-ratio-desktop');
      var m = active.style.getPropertyValue('--slide-aspect-ratio-mobile');
      if (d) trackEl.style.setProperty('--slide-aspect-ratio-desktop', d.trim());
      if (m) trackEl.style.setProperty('--slide-aspect-ratio-mobile', m.trim());
    }

    function updateActiveClass(idx) {
      slideEls.forEach(function (slideEl, i) {
        var isActive = i === idx;
        slideEl.classList.toggle('slide--active', isActive);
        slideEl.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        setSlideAria(slideEl, i);
      });
      progressItems.forEach(function (item, i) {
        var isActive = i === idx;
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
        item.setAttribute('tabindex', isActive ? '0' : '-1');
      });
      syncAdaptTrackAspect(idx);
    }

    function goToSlide(idx, fromUserAction, direction) {
      // Rapid-click safety: cancel any in-flight outgoing transition before
      // starting a new one. Prevents stuck .slide--outgoing classes and
      // stuck data-transitioning (curtain_reveal gating attribute).
      if (state.outgoingTimer) {
        clearTimeout(state.outgoingTimer);
        state.outgoingTimer = null;
        var prevOutgoing = trackEl.querySelector('.slide--outgoing');
        if (prevOutgoing) prevOutgoing.classList.remove('slide--outgoing');
        rootEl.removeAttribute('data-transitioning');
      }

      // Phase D.3 — cardstack layout: the visible motion is the card flying
      // off + the stack rotating. Skip the class-toggle / outgoing-class /
      // data-transitioning machinery; translate the absolute target idx into
      // a series of unit-step rotations via advanceStack / retreatStack.
      // max_blocks: 8 in the schema bounds the loop at <= 4 iterations.
      // advanceStack / retreatStack early-return at the edge when enableLoop
      // is false, so the multi-step loop respects the loop bound automatically.
      if (state.layout === 'cardstack') {
        if (enableLoop) {
          if (idx < 0) idx = state.total - 1;
          if (idx >= state.total) idx = 0;
        } else {
          if (idx < 0) idx = 0;
          if (idx >= state.total) idx = state.total - 1;
        }
        if (idx === state.currentIdx && !fromUserAction) return;

        var fromIdx = state.currentIdx;
        var diff = idx - fromIdx;
        if (enableLoop) {
          if (diff > state.total / 2) diff -= state.total;
          if (diff < -state.total / 2) diff += state.total;
        }
        if (diff > 0) {
          for (var s = 0; s < diff; s++) advanceStack();
        } else if (diff < 0) {
          for (var t = 0; t < -diff; t++) retreatStack();
        }

        if (fromUserAction) {
          state.autoplayDone = false;
          restartAutoplay();
        }
        return;
      }

      // Phase D.2 — peek layout: native scroll handles motion. Bypass the
      // class-toggle / outgoing-class / data-transitioning machinery entirely.
      // Programmatic scroll via trackEl.scrollTo (mirrors component-product-carousel.js
      // pattern) — computing offset from getBoundingClientRect guarantees no
      // incidental vertical page scroll. IntersectionObserver eventually
      // reconciles state.currentIdx; we set it eagerly here so dots / progress
      // reflect intent during the smooth-scroll animation window.
      if (state.layout === 'peek') {
        if (enableLoop) {
          if (idx < 0) idx = state.total - 1;
          if (idx >= state.total) idx = 0;
        } else {
          if (idx < 0) idx = 0;
          if (idx >= state.total) idx = state.total - 1;
        }
        if (idx === state.currentIdx && !fromUserAction) return;
        var targetSlide = slideEls[idx];
        var trackBox = trackEl.getBoundingClientRect();
        var slideBox = targetSlide.getBoundingClientRect();
        var slideCenterX = slideBox.left + slideBox.width / 2;
        var trackCenterX = trackBox.left + trackBox.width / 2;
        var offset = trackEl.scrollLeft + (slideCenterX - trackCenterX);
        trackEl.scrollTo({ left: offset, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
        state.currentIdx = idx;
        updateActiveClass(idx);
        updateArrowDisabled();
        if (fromUserAction) {
          state.autoplayDone = false;
          restartAutoplay();
        }
        return;
      }

      if (enableLoop) {
        if (idx < 0) idx = state.total - 1;
        if (idx >= state.total) idx = 0;
      } else {
        if (idx < 0) idx = 0;
        if (idx >= state.total) idx = state.total - 1;
      }
      if (idx === state.currentIdx && !fromUserAction) {
        return;
      }

      // Capture outgoing slide BEFORE active-class flip.
      var outgoingSlide = slideEls[state.currentIdx];

      // Set direction on root — read by CSS attribute selector for the slide effect.
      // Harmless for fade (CSS doesn't consult direction). curtain_reveal gates on
      // data-transitioning instead so its keyframe animation doesn't fire at first
      // paint when .section-slideshow--initialized is added (F-3 option c).
      if (direction === 'prev') {
        rootEl.setAttribute('data-direction', 'prev');
      } else {
        rootEl.setAttribute('data-direction', 'next');
      }
      rootEl.setAttribute('data-transitioning', 'true');

      updateActiveClass(idx);

      // Tag the leaving slide with .slide--outgoing for the transition duration.
      // CSS rules for fade/slide/curtain_reveal consume this class. Timer-driven
      // removal keeps the leaving slide visible (and animating) during the transition.
      // Same setTimeout clears data-transitioning so curtain_reveal disarms.
      if (outgoingSlide && outgoingSlide !== slideEls[idx]) {
        outgoingSlide.classList.add('slide--outgoing');
        state.outgoingTimer = setTimeout(function () {
          outgoingSlide.classList.remove('slide--outgoing');
          rootEl.removeAttribute('data-transitioning');
          state.outgoingTimer = null;
        }, transitionDuration);
      } else {
        // Same-slide click (user re-clicked the active dot). No real transition
        // runs, so clear data-transitioning synchronously to avoid arming the
        // curtain_reveal animation rule on a no-op flip.
        rootEl.removeAttribute('data-transitioning');
      }

      state.currentIdx = idx;
      updateArrowDisabled();

      if (fromUserAction) {
        state.autoplayDone = false;
        restartAutoplay();
      }
    }

    function next() { goToSlide(state.currentIdx + 1, true, 'next'); }
    function prev() { goToSlide(state.currentIdx - 1, true, 'prev'); }

    function autoplayTick() {
      // Phase D.2 — peek: pause autoplay while the user is actively scrolling
      // the track. Resumes naturally on the next interval after the 150ms
      // scroll-end debounce fires (see setupPeekScrollListener).
      if (state.layout === 'peek' && state.userScrolling) return;
      // Phase D.3 — cardstack: pause autoplay while a drag is active or while
      // a commit animation is in flight. Next interval fires normally once
      // dragReleasing clears (after state.cardstackAnimDuration ms).
      if (state.layout === 'cardstack' && (state.dragActive || state.dragReleasing)) return;
      if (state.isHovered || state.isFocused || !state.isVisible || !state.isPageVisible || !state.isWindowFocused) {
        return;
      }
      var nextIdx = state.currentIdx + 1;
      if (nextIdx >= state.total) {
        if (enableLoop) {
          nextIdx = 0;
        } else {
          // F9 amendment — autoplay stops at last slide when looping is off.
          state.autoplayDone = true;
          stopAutoplay();
          return;
        }
      }
      goToSlide(nextIdx, false, 'next');
    }

    function startAutoplay() {
      if (!enableAutoplay || prefersReducedMotion) return;
      if (state.autoplayDone) return;
      if (state.autoplayTimer) return;
      state.autoplayTimer = setInterval(autoplayTick, autoplayInterval);
    }

    function stopAutoplay() {
      if (state.autoplayTimer) {
        clearInterval(state.autoplayTimer);
        state.autoplayTimer = null;
      }
    }

    function restartAutoplay() {
      stopAutoplay();
      startAutoplay();
    }

    // Phase D.2 — peek layout setup helpers.
    // IntersectionObserver detects the centered slide as the user scrolls
    // natively (touch swipe, scrollbar drag, keyboard arrows in browser default
    // scroll). Pattern mirrors component-product-carousel.js (Slice 10 Phase G)
    // with one refinement: pick the highest-ratio intersecting entry per
    // callback batch to avoid flicker during fast scroll inertia when two
    // slides briefly satisfy threshold:0.6 in the same callback.
    function setupPeekObserver() {
      if (!('IntersectionObserver' in window)) return;
      state.peekObserver = new IntersectionObserver(function (entries) {
        var best = null;
        entries.forEach(function (entry) {
          if (entry.intersectionRatio >= 0.6) {
            if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
          }
        });
        if (best) {
          var idx = slideEls.indexOf(best.target);
          if (idx !== -1 && idx !== state.currentIdx) {
            state.currentIdx = idx;
            updateActiveClass(idx);
            updateArrowDisabled();
          }
        }
      }, { root: trackEl, threshold: 0.6 });
      slideEls.forEach(function (s) { state.peekObserver.observe(s); });
    }

    // 150ms scroll-end debounce: user actively scrolling -> autoplay paused.
    // Once scrolling settles, the next autoplay tick resumes normally.
    function setupPeekScrollListener() {
      trackEl.addEventListener('scroll', function () {
        state.userScrolling = true;
        if (state.scrollEndTimer) clearTimeout(state.scrollEndTimer);
        state.scrollEndTimer = setTimeout(function () {
          state.userScrolling = false;
        }, 150);
      }, { passive: true });
    }

    // Phase D.3 — cardstack stack rotation helpers.
    // advanceStack moves the top slide to the bottom (commit a swipe forward
    // / arrow-next / autoplay). retreatStack moves the bottom slide to the
    // top (arrow-prev / dot click backward). enableLoop=false short-circuits
    // at the natural edges. Both call updateStackVisuals to re-decorate the
    // DOM with data-stack-pos / data-stack-hidden, then push state.currentIdx
    // through the shared updateActiveClass + updateArrowDisabled path so
    // ARIA + dot active state + arrow disabled state reconcile uniformly.
    function advanceStack() {
      if (!enableLoop && state.stack[0] === state.total - 1) return;
      state.stack = state.stack.slice(1).concat(state.stack[0]);
      updateStackVisuals();
      state.currentIdx = state.stack[0];
      updateActiveClass(state.currentIdx);
      updateArrowDisabled();
    }

    function retreatStack() {
      if (!enableLoop && state.stack[0] === 0) return;
      state.stack = [state.stack[state.stack.length - 1]].concat(state.stack.slice(0, -1));
      updateStackVisuals();
      state.currentIdx = state.stack[0];
      updateActiveClass(state.currentIdx);
      updateArrowDisabled();
    }

    function updateStackVisuals() {
      slideEls.forEach(function (slideEl) {
        slideEl.removeAttribute('data-stack-pos');
        slideEl.removeAttribute('data-stack-hidden');
        slideEl.style.transform = '';
      });
      for (var i = 0; i < state.stack.length; i++) {
        var slideEl = slideEls[state.stack[i]];
        if (!slideEl) continue;
        if (i <= 2) {
          slideEl.setAttribute('data-stack-pos', String(i));
        } else {
          slideEl.setAttribute('data-stack-hidden', '');
        }
      }
    }

    // Phase D.3 — pointer-driven drag on the top card. Pointer Events API
    // uniformly captures mouse / touch / stylus. Listeners attach to every
    // slide; the handlers internally short-circuit for non-top slides so we
    // never need to rebind on stack rotation. Hidden cards (display: none)
    // never fire pointerdown so the check is mostly defensive.
    function setupCardstackDrag() {
      var thresholdDesktop = 120;
      var thresholdMobile = 60;
      var rotationFactor = 0.06;
      var dyDamping = 0.3;
      var offscreenDistance = 1200;

      function getThreshold() {
        return window.innerWidth < 750 ? thresholdMobile : thresholdDesktop;
      }

      function onPointerDown(e) {
        if (state.dragReleasing) return;
        var topSlide = slideEls[state.stack[0]];
        if (!topSlide || e.currentTarget !== topSlide) return;
        // Preserve clicks/taps on interactive descendants of the slide
        // (merchant-placed CTAs, links, form controls). Matches the mousedown
        // handler pattern below.
        if (e.target.closest && e.target.closest('a, button, [role="button"], input, textarea, select, label')) return;
        topSlide.setPointerCapture(e.pointerId);
        state.dragActive = true;
        state.dragPointerId = e.pointerId;
        state.dragStartX = e.clientX;
        state.dragStartY = e.clientY;
        state.dragDx = 0;
        state.dragDy = 0;
        rootEl.setAttribute('data-cardstack-dragging', 'true');
      }

      function onPointerMove(e) {
        if (!state.dragActive || e.pointerId !== state.dragPointerId) return;
        state.dragDx = e.clientX - state.dragStartX;
        state.dragDy = e.clientY - state.dragStartY;
        var topSlide = slideEls[state.stack[0]];
        if (!topSlide) return;
        var rot = state.dragDx * rotationFactor;
        topSlide.style.transform =
          'translate(' + state.dragDx + 'px, ' + (state.dragDy * dyDamping) + 'px) rotate(' + rot + 'deg)';
      }

      function onPointerUp(e) {
        if (!state.dragActive || e.pointerId !== state.dragPointerId) return;
        var threshold = getThreshold();
        var topSlide = slideEls[state.stack[0]];
        rootEl.removeAttribute('data-cardstack-dragging');
        state.dragActive = false;
        state.dragPointerId = null;

        if (Math.abs(state.dragDx) > threshold) {
          commitAdvance(state.dragDx > 0 ? offscreenDistance : -offscreenDistance, topSlide);
        } else {
          // Snap back: clear inline transform; CSS transition handles easing.
          if (topSlide) topSlide.style.transform = '';
          state.dragDx = 0;
          state.dragDy = 0;
        }
      }

      function commitAdvance(offscreenX, topSlide) {
        state.dragReleasing = true;
        if (topSlide) {
          topSlide.style.transform = 'translate(' + offscreenX + 'px, 0) rotate(0deg)';
        }
        if (state.cardstackTimer) clearTimeout(state.cardstackTimer);
        state.cardstackTimer = setTimeout(function () {
          advanceStack();
          if (topSlide) topSlide.style.transform = '';
          state.dragDx = 0;
          state.dragDy = 0;
          state.dragReleasing = false;
        }, state.cardstackAnimDuration);
      }

      slideEls.forEach(function (slideEl) {
        slideEl.addEventListener('pointerdown', onPointerDown);
        slideEl.addEventListener('pointermove', onPointerMove);
        slideEl.addEventListener('pointerup', onPointerUp);
        slideEl.addEventListener('pointercancel', onPointerUp);
      });
    }

    if (state.layout === 'peek') {
      setupPeekObserver();
      setupPeekScrollListener();
    }

    if (state.layout === 'cardstack') {
      state.stack = slideEls.map(function (_, i) { return i; });
      updateStackVisuals();
      setupCardstackDrag();
    }

    if (prevBtn) prevBtn.addEventListener('click', prev);
    if (nextBtn) nextBtn.addEventListener('click', next);

    progressItems.forEach(function (item) {
      item.addEventListener('click', function () {
        var idx = parseInt(item.getAttribute('data-slide-index'), 10);
        if (!isNaN(idx)) {
          var dir = idx > state.currentIdx ? 'next' : (idx < state.currentIdx ? 'prev' : 'jump');
          goToSlide(idx, true, dir);
        }
      });
    });

    if (pauseOnHover) {
      rootEl.addEventListener('mouseenter', function () { state.isHovered = true; });
      rootEl.addEventListener('mouseleave', function () { state.isHovered = false; });
    }

    rootEl.addEventListener('focusin', function () { state.isFocused = true; });
    rootEl.addEventListener('focusout', function () { state.isFocused = false; });

    rootEl.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
      }
    });

    var touchStartX = 0;
    var touchStartTime = 0;
    trackEl.addEventListener('touchstart', function (e) {
      // Phase D.2 — peek: native scroll-snap handles touch swipe; the manual
      // 50px/0.3-velocity logic below would race the browser's own scroll.
      // Phase D.3 — cardstack: drag is owned by setupCardstackDrag via
      // Pointer Events on the top slide; the touch path here would double-fire.
      if (state.layout === 'peek' || state.layout === 'cardstack') return;
      if (!e.touches || e.touches.length === 0) return;
      touchStartX = e.touches[0].clientX;
      touchStartTime = Date.now();
    }, { passive: true });
    trackEl.addEventListener('touchend', function (e) {
      if (state.layout === 'peek' || state.layout === 'cardstack') return;
      if (!e.changedTouches || e.changedTouches.length === 0) return;
      var deltaX = e.changedTouches[0].clientX - touchStartX;
      var deltaTime = Date.now() - touchStartTime;
      var velocity = deltaTime > 0 ? Math.abs(deltaX) / deltaTime : 0;
      if (Math.abs(deltaX) > 50 || velocity > 0.3) {
        if (deltaX < 0) {
          next();
        } else {
          prev();
        }
      }
    }, { passive: true });

    // Mouse drag (PC users) — same 50px / 0.3 velocity threshold as touch.
    // One-shot mouseup listener attaches per drag and removes itself on fire,
    // so we never leak a persistent document-level listener.
    var mouseDragStartX = 0;
    var mouseDragStartTime = 0;

    trackEl.addEventListener('mousedown', function (e) {
      // Phase D.2 — peek: native scrollbar drag + wheel + click-and-drag on the
      // scrollbar handle gives the user mouse-side navigation. No manual drag.
      // Phase D.3 — cardstack: drag is owned by setupCardstackDrag via Pointer
      // Events on the top slide; this mousedown path would double-fire.
      if (state.layout === 'peek' || state.layout === 'cardstack') return;
      if (e.button !== 0) return;
      if (e.target.closest('[data-slideshow-prev], [data-slideshow-next], [data-slideshow-progress-item]')) return;
      if (e.target.closest('a, button, [role="button"]')) return;

      mouseDragStartX = e.clientX;
      mouseDragStartTime = Date.now();

      function onMouseUp(upE) {
        document.removeEventListener('mouseup', onMouseUp);
        var deltaX = upE.clientX - mouseDragStartX;
        var deltaTime = Date.now() - mouseDragStartTime;
        var velocity = deltaTime > 0 ? Math.abs(deltaX) / deltaTime : 0;
        if (Math.abs(deltaX) > 50 || velocity > 0.3) {
          if (deltaX < 0) next();
          else prev();
        }
      }

      document.addEventListener('mouseup', onMouseUp);
    });

    // Block native HTML drag (image ghost, text drag) on the track.
    trackEl.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          state.isVisible = entry.isIntersecting;
        });
      }, { threshold: 0.5 });
      io.observe(rootEl);
    }

    // Page Visibility API — pause autoplay when browser tab/window is hidden.
    // Listener is document-scoped + lives for page lifetime; no destroy hook
    // exists for slideshows, but the handler is no-op cheap.
    document.addEventListener('visibilitychange', function () {
      state.isPageVisible = !document.hidden;
    });

    // Window blur/focus — catches multi-monitor case where tab stays technically
    // visible per Page Visibility spec but the browser window itself is not focused.
    window.addEventListener('blur', function () {
      state.isWindowFocused = false;
    });
    window.addEventListener('focus', function () {
      state.isWindowFocused = true;
    });

    // Reconcile initial state — first slide already has .slide--active server-side.
    updateActiveClass(0);
    updateArrowDisabled();
    // PERF-PASS P2.1 R1 — gate the INITIAL autoplay start on window load so a
    // pre-load tick can't promote a lazy non-first slide to the lab LCP element.
    // User-driven advances (restartAutoplay) are unaffected.
    if (document.readyState === 'complete') {
      startAutoplay();
    } else {
      window.addEventListener('load', startAutoplay, { once: true });
    }

    // Mark slideshow as initialized — gates Phase C.5 transition CSS rules so the
    // server-rendered first slide does NOT animate at page load. fade/slide are
    // gated by this flag alone (transition: doesn't fire on initial paint).
    // curtain_reveal is additionally gated by [data-transitioning] (set only
    // during actual goToSlide lifecycle), so the keyframe animation does not
    // fire here either — see goToSlide() body for the lifecycle.
    rootEl.classList.add('section-slideshow--initialized');
  }

  function initAll(scopeEl) {
    var root = scopeEl || document;
    var slideshows = root.querySelectorAll('[data-slideshow-id]');
    slideshows.forEach(initSlideshow);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) initAll(event.target);
  });
})();
